import loadable from "@loadable/component";
import { inject, observer } from "mobx-react";
import { useEffect } from "react";
import type { Store } from "@inferno/renderer-shared-core";
import { ShareButtons } from "../../components/integrations/ShareButtons.component";
import { RemoteList } from "../../components/remote/RemoteList.component";
import type { BlockFragment, ChartV2Fragment } from "@ihr-radioedit/inferno-webapi";
import { reverseRoute } from "@inferno/renderer-shared-core";
import { chartListsCursor } from "../../services/Charts";
import { Card, Container, Grid, Heading, LoadMoreFromCursor } from "../../ui";
import "./ChartListsCollection.style.scss";
import { ChartListsCollectionFilter } from "./ChartListsCollectionFilter.component";

const ContentTile = loadable(() => import("../../components/tiles/ContentTile.component"));

interface ChartListsProps {
  setMonth: (v: number | null) => void;
  setYear: (v: number | null) => void;
  chart: ChartV2Fragment;
  block: BlockFragment;
  title?: string | null;
  month: number | null;
  year: number | null;
  isPrimary: boolean;
  store?: Store;
}

export const ChartListsCollection = inject("store")(
  observer(({ chart, title, store, block, isPrimary, ...selectorProps }: ChartListsProps) => {
    if (!store) {
      return null;
    }

    if (!chart) {
      return null;
    }

    useEffect(() => {
      if (block) {
        store.storeBlock({
          ...block,
          resolved: chart,
        });
      }
    });

    const { cache, request, site } = store;

    const cursor = chartListsCursor(cache)(chart.lists, chart.slug);

    const path = reverseRoute(site, "chart", { chart_slug: chart.slug }) || `/charts/${chart.slug}/`;

    const shareUrl = site.getShareUrl(request, path);

    return (
      <Container className="component-chart-lists-collection">
        <header className="content-header chart-lists-header">
          <Heading level={1}>{title || chart.title}</Heading>
          <div className="chart-sub-header">
            <ShareButtons title={chart.title} url={shareUrl} className="chart-share" />
            <ChartListsCollectionFilter chartDates={chart.dates} {...selectorProps} />
          </div>
        </header>
        <RemoteList cursor={cursor} showLoading={isPrimary}>
          {({ data, hasNext, loading, next }) => (
            <section className="chart-lists-items">
              <Grid columns={4} gap={false}>
                {data.map(list => (
                  <Card key={list.slug}>
                    <ContentTile
                      thumb={list.image ?? list.topItems ?? ""}
                      title={list.title}
                      url={`${list.slug}/`}
                      layout="square"
                      slug={list.slug}
                    />
                  </Card>
                ))}
              </Grid>
              <LoadMoreFromCursor sectionName="chart" hasNext={hasNext} loading={loading} nextFn={next} />
            </section>
          )}
        </RemoteList>
      </Container>
    );
  }),
);

export default ChartListsCollection;
